import { useEffect, useState } from 'react';
import billingPreviewService from '@/services/dev/billingPreviewService';
import { useAuthContext } from '@/auth/useAuthContext';
import { isSuperAdmin } from '@/auth/accessControl';

/**
 * Tester tool: flips the node between the paid and paywalled experience.
 * Renders only once the backend confirms the preview is enabled, so it stays
 * invisible in production, where those endpoints 404.
 *
 * Never probes on a super-admin session: that token fails auth:sanctum, and
 * the 401 would trip the global logout redirect in apiService.
 */

const LAUNCHER_STYLE = {
  position: 'fixed',
  bottom: '14px',
  right: '14px',
  zIndex: 9999,
  width: '34px',
  height: '34px',
  borderRadius: '50%',
  border: '1px solid rgba(0,0,0,0.15)',
  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
  backgroundColor: '#ffffff',
  fontSize: '15px',
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  opacity: 0.45,
  transition: 'opacity 0.15s ease',
};

const BACKDROP_STYLE = {
  position: 'fixed',
  inset: 0,
  zIndex: 10000,
  backgroundColor: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const CARD_STYLE = {
  width: 'min(420px, calc(100vw - 32px))',
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  color: '#111827',
  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  padding: '20px',
  fontSize: '13px',
};

const OPTION_STYLE = (selected, disabled) => ({
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '12px 14px',
  marginTop: '10px',
  borderRadius: '8px',
  border: selected ? '2px solid #2563eb' : '1px solid #d1d5db',
  backgroundColor: selected ? '#eff6ff' : '#ffffff',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled && !selected ? 0.6 : 1,
});

const BillingPreviewToggle = () => {
  const { auth, updateUser } = useAuthContext();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const user = auth?.user;

  useEffect(() => {
    if (!user || isSuperAdmin(user)) return undefined;

    let cancelled = false;
    billingPreviewService
      .getState()
      .then((res) => {
        if (!cancelled && res?.data?.enabled) setState(res.data);
      })
      .catch(() => {
        // Disabled (production) or unreachable — stay hidden.
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !busy) setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, busy]);

  if (!state) return null;

  const active = state.mode === 'active';

  const selectMode = async (targetActive) => {
    if (busy) return;
    if (targetActive === active) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      const res = await billingPreviewService.toggle();
      const next = res?.data;
      if (next) {
        // The provider hydrates the user from localStorage on load, so the
        // stored company_is_active must be updated before the reload or the
        // SPA-side redirect keeps using the stale value.
        updateUser({ company_is_active: next.is_active }, true);
        window.location.assign('/');
        return;
      }
      setBusy(false);
    } catch {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Billing preview (test tool, staging/local only)"
        style={LAUNCHER_STYLE}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.45; }}
      >
        🧪
      </button>

      {open && (
        <div
          style={BACKDROP_STYLE}
          onClick={() => { if (!busy) setOpen(false); }}
        >
          <div style={CARD_STYLE} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: '14px' }}>🧪 Billing preview</strong>
              <button
                type="button"
                onClick={() => { if (!busy) setOpen(false); }}
                style={{ border: 'none', background: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <p style={{ margin: '8px 0 4px', color: '#6b7280' }}>
              Test tool (staging/local only). Switches this whole node between
              the paid and unpaid experience — it affects every user on this
              environment.
            </p>

            <button
              type="button"
              disabled={busy}
              onClick={() => selectMode(true)}
              style={OPTION_STYLE(active, busy)}
            >
              <strong style={{ color: '#16a34a' }}>Billing active — paid view</strong>
              {active && <span style={{ float: 'right', color: '#2563eb' }}>current</span>}
              <div style={{ color: '#6b7280', marginTop: '2px' }}>
                Normal app, as a subscribed firm sees it.
              </div>
            </button>

            <button
              type="button"
              disabled={busy}
              onClick={() => selectMode(false)}
              style={OPTION_STYLE(!active, busy)}
            >
              <strong style={{ color: '#dc2626' }}>Billing inactive — paywall view</strong>
              {!active && <span style={{ float: 'right', color: '#2563eb' }}>current</span>}
              <div style={{ color: '#6b7280', marginTop: '2px' }}>
                Admins land on the purchase page; other staff are blocked (402)
                until billing is reactivated. Use this button again to switch back.
              </div>
            </button>

            {busy && (
              <p style={{ margin: '10px 0 0', color: '#6b7280' }}>Switching and reloading…</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export { BillingPreviewToggle };
